/**
 * useHomePageData — fetches recently visited, recommended and trending shops
 * for the personalized home page sections.
 */

import { useState, useEffect } from 'react'
import { apiUrl } from '@/config'
import { useAuthStore } from '@/stores/authStore'
import { getGuestId } from '@utils/guestIdentity'

export interface ShopCard {
  shopId: string
  realShopId?: string  // Resolved shop ID (for poster URL); shopId may be dataset ID
  shopName: string
  posterUrl?: string
  category?: string
  address?: string
  lastVisit?: string
  visitCount?: number
  score?: number
}

interface HomePageData {
  recentShops: ShopCard[]
  recommendations: ShopCard[]
  trending: ShopCard[]
  loading: boolean
}

export function useHomePageData(): HomePageData {
  const [recentShops, setRecentShops] = useState<ShopCard[]>([])
  const [recommendations, setRecommendations] = useState<ShopCard[]>([])
  const [trending, setTrending] = useState<ShopCard[]>([])
  const [loading, setLoading] = useState(true)

  const user = useAuthStore((s) => s.user)

  useEffect(() => {
    let cancelled = false
    const userId = user?.id || getGuestId()
    const token = localStorage.getItem('dukkan_token')
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`

    async function load() {
      try {
        const [recentRes, recoRes, trendRes] = await Promise.allSettled([
          fetch(apiUrl(`/api/activity/recent-shops?userId=${encodeURIComponent(userId)}`), { headers }),
          fetch(apiUrl(`/api/activity/recommendations?userId=${encodeURIComponent(userId)}`), { headers }),
          fetch(apiUrl('/api/shops/trending'), { headers }),
        ])

        if (cancelled) return

        if (recentRes.status === 'fulfilled' && recentRes.value.ok) {
          const data = await recentRes.value.json()
          setRecentShops(data.shops || [])
        }
        if (recoRes.status === 'fulfilled' && recoRes.value.ok) {
          const data = await recoRes.value.json()
          setRecommendations(data.shops || [])
        }
        if (trendRes.status === 'fulfilled' && trendRes.value.ok) {
          const data = await trendRes.value.json()
          setTrending(data.shops || [])
        }
      } catch (err) {
        console.warn('[useHomePageData] Failed to load:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [user?.id])

  return { recentShops, recommendations, trending, loading }
}
